import { TSeatCoverDataDB } from '@/lib/db/seat-covers';
import { IProductData } from '@/utils';
import { yearInYearGeneration } from './date';

// Union type for both product data types
type ProductData = TSeatCoverDataDB | IProductData;

// Type for selection criteria
type SelectionCriteria = {
  display_color?: string;
  year?: string;
  year_generation?: string;
  model_detail?: string;
  submodel1?: string;
  submodel2?: string;
  submodel3?: string;
  display_set?: string;
  seat_type_option?: string;
  seat_subtype?: string;
};

/**
 * Filter products based on selection criteria
 * @param products - Array of products to filter
 * @param selections - Current selection criteria
 * @returns Filtered array of products
 */
export function filterProductsBySelections<T extends ProductData>(
  products: T[],
  selections: SelectionCriteria
): T[] {
  return products.filter((product) => {
    // Filter by color
    if (selections.display_color && product.display_color !== selections.display_color) {
      return false;
    }

    // Filter by year (check if year is in year_generation range)
    if (selections.year && product.year_generation) {
      if (!yearInYearGeneration(selections.year, product.year_generation)) {
        return false;
      }
    }

    // Filter by model_detail
    if (selections.model_detail && product.model_detail !== selections.model_detail) {
      return false;
    }

    // Filter by submodels
    if (selections.submodel1 && product.submodel1 !== selections.submodel1) {
      return false;
    }

    if (selections.submodel2 && product.submodel2 !== selections.submodel2) {
      return false;
    }

    if (selections.submodel3 && product.submodel3 !== selections.submodel3) {
      return false;
    }

    // Filter by display_set (for seat covers)
    if (selections.display_set && 'display_set' in product) {
      if (product.display_set !== selections.display_set) {
        return false;
      }
    }

    // Filter by seat_type_option (for seat covers)
    if (selections.seat_type_option && 'seat_type_option' in product) {
      if (product.seat_type_option !== selections.seat_type_option) {
        return false;
      }
    }

    // Filter by seat_subtype (for seat covers)
    if (selections.seat_subtype && 'seat_subtype' in product) {
      if (product.seat_subtype !== selections.seat_subtype) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Get unique values for a specific field from an array of products
 * @param products - Array of products
 * @param field - Field to extract unique values from
 * @returns Array of unique values
 */
export function getUniqueFieldValues<T extends ProductData, K extends keyof T>(
  products: T[],
  field: K
): Array<T[K]> {
  const uniqueValues = new Set<T[K]>();
  
  products.forEach((product) => {
    const value = product[field];
    if (value !== undefined && value !== null && value !== '') {
      uniqueValues.add(value);
    }
  });
  
  return Array.from(uniqueValues);
}

/**
 * Get auto-selected value if only one option exists
 * @param products - Array of products to check
 * @param field - Field to check for unique values
 * @param currentSelections - Current selection criteria to filter by
 * @returns The single value if only one exists, otherwise empty string
 */
export function getAutoSelectedValue<T extends ProductData>(
  products: T[],
  field: keyof T,
  currentSelections: SelectionCriteria
): string {
  // Filter products based on current selections
  const filteredProducts = filterProductsBySelections(products, currentSelections);
  
  // Get unique values for the field
  const uniqueValues = getUniqueFieldValues(filteredProducts, field);
  
  // If only one unique value exists, return it
  if (uniqueValues.length === 1) {
    return String(uniqueValues[0]);
  }
  
  return '';
}

/**
 * Get auto-selected values for multiple fields in sequence
 * Useful for dependent fields like submodel1 -> submodel2 -> submodel3
 * @param products - Array of products
 * @param fields - Array of fields to check in order
 * @param initialSelections - Initial selection criteria
 * @returns Object with auto-selected values for each field
 */
export function getSequentialAutoSelections<T extends ProductData>(
  products: T[],
  fields: Array<keyof T>,
  initialSelections: SelectionCriteria
): Record<string, string> {
  const autoSelections: Record<string, string> = {};
  const currentSelections = { ...initialSelections };
  
  // Process each field in sequence
  for (const field of fields) {
    const autoValue = getAutoSelectedValue(products, field, currentSelections);
    
    if (autoValue) {
      autoSelections[String(field)] = autoValue;
      // Add this selection to current selections for next field
      currentSelections[String(field) as keyof SelectionCriteria] = autoValue;
    }
  }
  
  return autoSelections;
}